import { createContext, type FC, type PropsWithChildren, useContext, useMemo } from "react";

import { OwnerTypes } from "@/domain/_shared/lib/owner-types";

import { DatatableState, defaultDatatableState } from "../components/datatable/datatable-types";
import { type ActiveView } from "../models/active-view-model";
import { compareDatatableStates, getCleanedDatatableState } from "../models/datatable-state-model";
import { type StoredView } from "../models/stored-view-model";

import { useGetActiveView } from "./api/use-get-active-view";
import { useGetStoredViewList } from "./api/use-get-stored-view-list";

type CurrentViewContext = {
  workspaceId: string;
  viewKey: string;
  isLoading: boolean;
  currentActiveView?: ActiveView | null | undefined;
  currentStoredViewList: StoredView[];
  hasUnsavedChanges: boolean;
  baseDatatableState: DatatableState;
  currentDatatableState: DatatableState;
  currentStoredViewDatatableState: DatatableState | null;
};

const CurrentViewContext = createContext<CurrentViewContext | null>(null);

export interface CurrentViewContextProviderProps extends PropsWithChildren {
  workspaceId: string;
  viewKey: string;
  baseDatatableState?: DatatableState;
}

export const CurrentViewContextProvider: FC<CurrentViewContextProviderProps> = ({
  children,
  workspaceId,
  viewKey,
  baseDatatableState = defaultDatatableState,
}) => {
  const { activeView, loading: activeViewLoading } = useGetActiveView({
    workspaceId,
    key: viewKey,
  });
  const { storedViewList, loading: storedViewListLoading } = useGetStoredViewList({
    workspaceId,
    key: viewKey,
  });

  const isLoading = activeViewLoading || storedViewListLoading;

  const defaultDatatableState = useMemo(() => {
    const userDefaultView = storedViewList?.find((view) => view.default && view.ownerType === "USER");

    if (userDefaultView) {
      const cleanedDatatableState = getCleanedDatatableState(userDefaultView);

      if (cleanedDatatableState) {
        return {
          ...cleanedDatatableState,
          columnVisibility: { ...baseDatatableState.columnVisibility, ...cleanedDatatableState.columnVisibility },
        };
      }
    }

    const teamDefaultView = storedViewList?.find((view) => view.default && view.ownerType === OwnerTypes.TEAM);

    if (teamDefaultView) {
      const cleanedDatatableState = getCleanedDatatableState(teamDefaultView);

      if (cleanedDatatableState) {
        return {
          ...cleanedDatatableState,
          columnVisibility: { ...baseDatatableState.columnVisibility, ...cleanedDatatableState.columnVisibility },
        };
      }
    }

    const workspaceDefaultView = storedViewList?.find((view) => view.default && view.ownerType === "WORKSPACE");

    if (workspaceDefaultView) {
      const cleanedDatatableState = getCleanedDatatableState(workspaceDefaultView);
      if (cleanedDatatableState) {
        return {
          ...cleanedDatatableState,
          columnVisibility: { ...baseDatatableState.columnVisibility, ...cleanedDatatableState.columnVisibility },
        };
      }
    }
    return baseDatatableState;
  }, [baseDatatableState, storedViewList]);

  const currentDatatableState = useMemo(() => {
    // When a stored view is selected, use its state as the source of truth.
    // The activeView's own datatable fields may be stale relative to the stored view
    // (e.g. if the stored view was updated after the activeView was last saved).
    if (activeView?.storedView) {
      const cleanedDatatableState = getCleanedDatatableState(activeView.storedView);
      if (cleanedDatatableState) {
        return {
          ...cleanedDatatableState,
          columnVisibility: { ...baseDatatableState.columnVisibility, ...cleanedDatatableState.columnVisibility },
        };
      }
    }

    if (activeView) {
      const cleanedDatatableState = getCleanedDatatableState(activeView);

      if (cleanedDatatableState) {
        return {
          ...cleanedDatatableState,
          columnVisibility: { ...baseDatatableState.columnVisibility, ...cleanedDatatableState.columnVisibility },
        };
      }
    }

    return defaultDatatableState;
  }, [defaultDatatableState, activeView, baseDatatableState]);

  const currentStoredViewDatatableState = useMemo(() => {
    if (activeView?.storedView) {
      const cleanedDatatableState = getCleanedDatatableState(activeView.storedView);
      if (cleanedDatatableState) {
        return cleanedDatatableState;
      }
    }

    return null;
  }, [activeView]);

  const hasUnsavedChanges = useMemo(() => {
    return !compareDatatableStates(currentDatatableState, currentStoredViewDatatableState ?? baseDatatableState);
  }, [currentDatatableState, currentStoredViewDatatableState, baseDatatableState]);

  return (
    <CurrentViewContext.Provider
      value={{
        workspaceId,
        viewKey,
        isLoading,
        currentActiveView: activeView,
        currentStoredViewList: storedViewList ?? [],
        hasUnsavedChanges,
        baseDatatableState,
        currentDatatableState,
        currentStoredViewDatatableState,
      }}
    >
      {children}
    </CurrentViewContext.Provider>
  );
};

export const useCurrentViewContext = () => {
  const context = useContext(CurrentViewContext);

  if (!context) {
    throw new Error("useCurrentViewContext must be used within a CurrentViewContextProvider");
  }

  return context;
};

export const useUnsafeCurrentViewContext = () => useContext(CurrentViewContext);
